import type { HeavenlyStem } from '../types/index';
import type { TenGod, TenGodInfo, DayMasterInfo, TenGodMapping } from '../types/index';
import type { FourPillars, PillarPosition } from '../types/index';
import { getStemElement, getElementRelationship } from '../core/five-elements';
import { getStemEumyang } from '../core/eumyang';
import { getHiddenStems } from '../core/hidden-stems';

/**
 * 일간 대비 대상 천간의 십신을 판정한다.
 *
 * 오행 관계 기반:
 * - 동일 오행(비겁): 동일 음양 → 비견, 상이 음양 → 겁재
 * - 일간이 생(식상): 동일 음양 → 식신, 상이 음양 → 상관
 * - 대상이 생(인성): 동일 음양 → 편인, 상이 음양 → 정인
 * - 일간이 극(재성): 동일 음양 → 편재, 상이 음양 → 정재
 * - 대상이 극(관성): 동일 음양 → 편관, 상이 음양 → 정관
 */
export function getTenGod(dayMaster: HeavenlyStem, targetStem: HeavenlyStem): TenGod {
  const dmElement = getStemElement(dayMaster);
  const targetElement = getStemElement(targetStem);
  const samePolarity = getStemEumyang(dayMaster) === getStemEumyang(targetStem);

  const relationship = getElementRelationship(dmElement, targetElement);

  switch (relationship) {
    case 'same':
      return samePolarity ? '비견' : '겁재';
    case 'generates': // DM generates target = 식상
      return samePolarity ? '식신' : '상관';
    case 'generated': // target generates DM = 인성
      return samePolarity ? '편인' : '정인';
    case 'overcomes': // DM overcomes target = 재성
      return samePolarity ? '편재' : '정재';
    case 'overcome': // target overcomes DM = 관성
      return samePolarity ? '편관' : '정관';
  }
}

/**
 * 사주 네 기둥의 십신을 분석한다.
 */
export function analyzeTenGods(fourPillars: FourPillars, noHour?: boolean): TenGodMapping {
  const dayMaster = fourPillars.day.stem;

  const positions: readonly PillarPosition[] = noHour ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hour'];

  const result: Record<string, TenGodInfo | DayMasterInfo> = {};
  const hiddenResult: Record<string, TenGodInfo[]> = {};

  for (const pos of positions) {
    const pillar = fourPillars[pos];

    // 일간은 십신을 부여하지 않음 (나 자신)
    if (pos === 'day') {
      result[pos] = {
        stem: pillar.stem,
        tenGod: null,
      };
    } else {
      result[pos] = {
        stem: pillar.stem,
        tenGod: getTenGod(dayMaster, pillar.stem),
      };
    }

    // Hidden stems ten gods
    const hidden = getHiddenStems(pillar.branch);
    hiddenResult[pos] = hidden.map((stem) => ({
      stem,
      tenGod: getTenGod(dayMaster, stem),
    }));
  }

  return {
    year: result['year'] as TenGodInfo,
    month: result['month'] as TenGodInfo,
    day: result['day'] as DayMasterInfo,
    ...(!noHour && { hour: result['hour'] as TenGodInfo }),
    hiddenStems: hiddenResult as Record<PillarPosition, TenGodInfo[]>,
  };
}
